import type { Category, Product } from '@equinox/shared';
import type { AdminTranslator } from '../../i18n/index.js';
import { MoneyInput } from '../../components/MoneyInput.js';
import { ImageUploadField } from './ImageUploadField.js';
import { StockInput } from './StockInput.js';
import type { ProductDraft } from './productDraft.js';

export interface EditableProductRowProps {
  product: Product;
  categories: Category[];
  draft: ProductDraft;
  dirty: boolean;
  /** Already-translated message from the last save attempt, if this row failed. */
  error?: string;
  /** True while a save pass is running — every field in every row is locked, not just this one, so nothing shifts under the admin mid-save. */
  disabled: boolean;
  onChange(patch: Partial<ProductDraft>): void;
  t: AdminTranslator;
}

/**
 * One row of the products table in "Edición en tabla" (batch table-edit)
 * mode — every cell editable in place, no per-cell click-to-edit toggle (that
 * affordance is `ProductsPage`'s own, single-cell price/stock edit, only
 * shown outside this mode). See
 * docs/decisions/2026-07-31-products-table-edit-mode.md.
 *
 * Column order matches the read-only row exactly (selection, image, name,
 * SKU, category, price, stock, active, actions/status) so the header never
 * has to change shape between modes — only what is rendered under it does.
 *
 * Every control here is one line tall and sized by its column, never by its
 * own content: widths come from the table's `<colgroup>` (see `ProductsPage`)
 * and the inputs use the `compact` variant, which keeps captions, hints and
 * validation messages out of the document flow. Entering this mode is
 * therefore a repaint of the cells, not a reflow of the table — the
 * requirement in OneDev xyntara/equinox #1 and in ROLES-DESIGNER.md's layout
 * stability rule. Anything added to a cell here must fit one 40px line;
 * if it cannot, it belongs in an anchored overlay (`CellEditPopover`),
 * not stacked inside the cell.
 */
export function EditableProductRow({
  product,
  categories,
  draft,
  dirty,
  error,
  disabled,
  onChange,
  t,
}: EditableProductRowProps) {
  const category = categories.find((c) => c.id === draft.categoryId);
  const fieldLabel = (field: string) => t('products.tableEdit.fieldLabel', { field, name: product.name });

  return (
    <tr className={dirty ? 'row--dirty' : undefined}>
      <td />
      <td>
        <ImageUploadField
          label={draft.name || product.name}
          color={category?.color ?? 'var(--accent)'}
          imageUrl={draft.imageUrl}
          onImageUrlChange={(url) => onChange({ imageUrl: url })}
          disabled={disabled}
          size={40}
          showUrlField={false}
          compact
          ariaLabel={fieldLabel(t('media.upload'))}
        />
      </td>
      <td>
        <input
          value={draft.name}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={disabled}
          aria-label={fieldLabel(t('products.name'))}
        />
      </td>
      <td className="numeric">
        <input
          value={draft.sku}
          onChange={(e) => onChange({ sku: e.target.value })}
          disabled={disabled}
          aria-label={fieldLabel(t('products.sku'))}
        />
      </td>
      <td>
        <select
          value={draft.categoryId}
          onChange={(e) => onChange({ categoryId: e.target.value })}
          disabled={disabled}
          aria-label={fieldLabel(t('products.category'))}
        >
          <option value="">{t('products.allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </td>
      <td className="numeric">
        <MoneyInput
          label=""
          ariaLabel={fieldLabel(t('products.price'))}
          valueCents={draft.priceCents}
          onChange={(cents, valid) => onChange({ priceCents: cents, priceValid: valid })}
          // Priced by its options (the product form edits them): a lone price here would be overwritten.
          disabled={disabled || (product.options?.length ?? 0) > 0}
          compact
        />
      </td>
      <td className="numeric">
        {/*
          * Cost arrived on main wrapped in `<div style={{ width: 110 }}>` and
          * without `compact` — the shape every other cell in this row moved
          * away from. Converted on merge: a non-compact MoneyInput stacks
          * caption + field + hint into three lines inside a row whose height is
          * pinned, which is the mechanism behind issue #1. Width comes from the
          * column, not from the control.
          */}
        <MoneyInput
          label=""
          ariaLabel={fieldLabel(t('products.cost'))}
          valueCents={draft.costCents}
          onChange={(cents, valid) => onChange({ costCents: cents, costValid: valid || cents == null })}
          disabled={disabled}
          compact
        />
      </td>
      <td className="numeric">
        <StockInput
          label=""
          ariaLabel={fieldLabel(t('products.stock'))}
          valueQty={draft.stockQty}
          onChange={(qty, valid) => onChange({ stockQty: qty, stockValid: valid })}
          // Counted on its options (the product form edits those), not on the product.
          disabled={disabled || (product.options?.length ?? 0) > 0}
          compact
        />
      </td>
      <td className="numeric">
        <input
          type="number"
          className="numeric"
          style={{ textAlign: 'right', width: '100%' }}
          value={draft.sortOrder}
          onChange={(e) => onChange({ sortOrder: Number(e.target.value) })}
          disabled={disabled}
          aria-label={fieldLabel(t('products.sortOrder'))}
        />
      </td>
      <td>
        <input
          type="checkbox"
          checked={draft.active}
          onChange={(e) => onChange({ active: e.target.checked })}
          disabled={disabled}
          aria-label={fieldLabel(t('common.active'))}
        />
      </td>
      <td>
        {error ? (
          // A save failure is a badge with the reason on hover, not a
          // paragraph: the message is a full sentence and this column is
          // 120px wide, so rendering it inline would wrap and grow the row —
          // the same mechanism as the image hint in issue #1. The complete,
          // untruncated text is repeated per row in the page-level banner
          // (see `ProductsPage.saveTableEdits`), so nothing is hidden.
          <span className="badge badge--danger" role="alert" title={error}>
            {t('products.tableEdit.rowFailed')}
          </span>
        ) : dirty ? (
          <span className="badge badge--warn">{t('products.tableEdit.unsaved')}</span>
        ) : null}
      </td>
    </tr>
  );
}
